import { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getProxiedImageUrl } from '@/lib/image-proxy';

interface ImageViewerProps {
  images: { url: string; caption?: string }[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ImageViewer({
  images,
  initialIndex = 0,
  open,
  onOpenChange,
}: ImageViewerProps): JSX.Element {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset to initial index when dialog opens
  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'Escape') {
        onOpenChange(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, [open, goToPrevious, goToNext, onOpenChange]);

  const currentImage = images[currentIndex];

  if (!currentImage) return <></>;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl border-none bg-black/95 p-0 [&>button]:text-white [&>button]:hover:bg-white/20 [&>button]:opacity-100">
        <div className="relative flex flex-col">
          {/* Image container */}
          <div className="relative flex items-center justify-center p-4 pt-8">
            {/* Previous button */}
            {images.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 z-10 h-10 w-10 rounded-full text-white hover:bg-white/20"
                onClick={goToPrevious}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            )}

            {/* Image - uses 'large' preset (1200px) for full-screen viewing (FR-127) */}
            <img
              src={getProxiedImageUrl(currentImage.url, 'large')}
              alt={currentImage.caption ?? `Image ${String(currentIndex + 1)}`}
              className="max-h-[80vh] max-w-full object-contain"
              data-testid="image-viewer-main"
            />

            {/* Next button */}
            {images.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 z-10 h-10 w-10 rounded-full text-white hover:bg-white/20"
                onClick={goToNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            )}
          </div>

          {/* Caption and counter */}
          <div className="flex items-center justify-between px-4 pb-4 text-white">
            <span className="text-sm text-white/70">
              {currentIndex + 1} / {images.length}
            </span>
            {currentImage.caption && (
              <span className="text-sm">{currentImage.caption}</span>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
